(() => {
  if (customElements.get('easygames-product-gallery-nav')) return;

  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');

  class EasyGamesProductGalleryNav extends HTMLElement {
    connectedCallback() {
      if (this.dataset.egReady === 'true') return;

      this.viewer = this.closest('slider-component');
      this.slider = this.viewer?.querySelector('.product__media-list.slider');
      this.rail = this.querySelector('.eg-product-gallery-dots');
      this.dots = Array.from(this.querySelectorAll('[data-eg-product-gallery-dot]'));
      this.prev = this.querySelector('[name="previous"]');
      this.next = this.querySelector('[name="next"]');

      if (!this.viewer || !this.slider || !this.dots.length) return;

      this.dataset.egReady = 'true';
      this.handleClick = this.onClick.bind(this);
      this.handleSlideChanged = this.onSlideChanged.bind(this);
      this.addEventListener('click', this.handleClick);
      this.viewer.addEventListener('slideChanged', this.handleSlideChanged);

      requestAnimationFrame(() => this.sync(this.currentIndex()));
    }

    disconnectedCallback() {
      if (this.handleClick) this.removeEventListener('click', this.handleClick);
      if (this.handleSlideChanged && this.viewer) {
        this.viewer.removeEventListener('slideChanged', this.handleSlideChanged);
      }
    }

    slides() {
      return Array.from(this.slider.querySelectorAll('.product__media-item')).filter(
        (slide) => !slide.hidden && slide.clientWidth > 0
      );
    }

    currentIndex() {
      const slides = this.slides();
      if (!slides.length) return 0;

      const center = this.slider.scrollLeft + this.slider.clientWidth / 2;
      let bestIndex = 0;
      let bestDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft + slide.clientWidth / 2 - center);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      return bestIndex;
    }

    onClick(event) {
      const dot = event.target.closest('[data-eg-product-gallery-dot]');
      if (!dot || !this.contains(dot)) return;

      const slides = this.slides();
      const index = Number.parseInt(dot.dataset.index, 10);
      const target = slides[index];
      if (!target) return;

      event.preventDefault();
      this.slider.scrollTo({
        left: target.offsetLeft,
        behavior: reducedMotion?.matches ? 'auto' : 'smooth',
      });
    }

    onSlideChanged(event) {
      const slides = this.slides();
      const current = event.detail?.currentElement;
      const index = current ? slides.indexOf(current) : this.currentIndex();
      this.sync(index >= 0 ? index : this.currentIndex(), slides.length);
    }

    sync(index, total = this.slides().length) {
      const safeTotal = Math.max(0, total);
      const safeIndex = safeTotal ? Math.min(Math.max(index, 0), safeTotal - 1) : 0;
      let activeDot = null;

      this.dots.forEach((dot, dotIndex) => {
        dot.hidden = dotIndex >= safeTotal;
        const active = dotIndex === safeIndex && dotIndex < safeTotal;
        dot.classList.toggle('is-active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
          activeDot = dot;
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (this.prev) this.prev.disabled = safeTotal <= 1 || safeIndex <= 0;
      if (this.next) this.next.disabled = safeTotal <= 1 || safeIndex >= safeTotal - 1;
      if (activeDot) this.centerDot(activeDot);
    }

    centerDot(dot) {
      if (!this.rail || this.rail.scrollWidth <= this.rail.clientWidth + 1) return;

      const left = Math.max(0, dot.offsetLeft - (this.rail.clientWidth - dot.clientWidth) / 2);
      this.rail.scrollTo({
        left,
        behavior: reducedMotion?.matches ? 'auto' : 'smooth',
      });
    }
  }

  customElements.define('easygames-product-gallery-nav', EasyGamesProductGalleryNav);
})();
